import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import HelpIcon from '../../../components/help/HelpIcon';
import LockPersonIcon from '@mui/icons-material/LockPerson';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import KeyIcon from '@mui/icons-material/Key';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import { PageContainer, Section, StatGrid } from '../components/AdminContentWrapper';
import { DataContext } from '../../../context/DataContext';
import { listRoles, useSecurityRolesService } from '../../../services/SecurityRolesService';
import { listPolicies } from '../../../services/SecurityPoliciesService';
import { listInvites } from '../../../services/UserInviteService';

// Iconos rotados para las tarjetas de rol -- puramente decorativo, no hay
// forma de que el backend indique un icono por rol.
const INVITES_COUNT_CAP = 100;

const ROLE_ICONS = [
  <LockPersonIcon sx={{ color: 'var(--wb-accent)' }} />,
  <AssignmentTurnedInIcon sx={{ color: 'var(--wb-success)' }} />,
  <KeyIcon sx={{ color: 'var(--wb-warning)' }} />
];

const emptyFormState = { name: '', description: '', policyIds: [] };

// Un Role ahora se compone adjuntando Políticas (bundles de permisos, ver
// SecurityPolicies.jsx) en vez de marcar permission keys planas una por una
// -- mismo cambio de modelo que create_role/update_role en
// functions/security-roles/app.py.
const RoleFormDialog = ({
  open,
  mode,
  initialRole,
  policies,
  policiesLoading,
  onClose,
  onSubmit,
  isSubmitting
}) => {
  const [formState, setFormState] = useState(emptyFormState);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      return;
    }
    if (mode === 'edit' && initialRole) {
      setFormState({
        name: initialRole.name || '',
        description: initialRole.description || '',
        policyIds: Array.isArray(initialRole.policy_ids) ? [...initialRole.policy_ids] : []
      });
    } else {
      setFormState(emptyFormState);
    }
    setError('');
  }, [open, mode, initialRole]);

  const togglePolicy = (policyId) => {
    setFormState((prev) => {
      const has = prev.policyIds.includes(policyId);
      return {
        ...prev,
        policyIds: has
          ? prev.policyIds.filter((id) => id !== policyId)
          : [...prev.policyIds, policyId]
      };
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!formState.name.trim()) {
      setError('El nombre del rol es obligatorio.');
      return;
    }
    setError('');
    await onSubmit(formState);
  };

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit,
        sx: {
          backgroundColor: 'var(--wb-bg-surface)',
          border: '1px solid var(--wb-border-16)'
        }
      }}
    >
      <DialogTitle sx={{ color: 'var(--wb-text-title)' }}>
        {mode === 'edit' ? 'Editar rol' : 'Nuevo rol'}
      </DialogTitle>
      <DialogContent dividers sx={{ borderColor: 'var(--wb-border)' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <TextField
          margin="normal"
          label="Nombre"
          fullWidth
          value={formState.name}
          onChange={(event) => setFormState((prev) => ({ ...prev, name: event.target.value }))}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <HelpIcon module="seguridad" topic="roles" anchor="nombre" />
              </InputAdornment>
            )
          }}
          sx={{
            '& .MuiInputBase-root': { color: 'var(--wb-text-title)', backgroundColor: 'var(--wb-bg-overlay-90)' },
            '& .MuiInputLabel-root': { color: 'var(--wb-muted-80)' }
          }}
        />
        <TextField
          margin="normal"
          label="Descripción"
          fullWidth
          multiline
          minRows={2}
          maxRows={4}
          value={formState.description}
          onChange={(event) => setFormState((prev) => ({ ...prev, description: event.target.value }))}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <HelpIcon module="seguridad" topic="roles" anchor="descripción" />
              </InputAdornment>
            )
          }}
          sx={{
            '& .MuiInputBase-root': { color: 'var(--wb-text-title)', backgroundColor: 'var(--wb-bg-overlay-90)' },
            '& .MuiInputLabel-root': { color: 'var(--wb-muted-80)' }
          }}
        />

        <Box sx={{ mt: 2 }}>
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
            <Typography sx={{ color: 'var(--wb-text)', fontWeight: 600 }}>
              Políticas adjuntas
            </Typography>
            <HelpIcon module="seguridad" topic="roles" anchor="políticas-adjuntas" />
          </Stack>

          {policiesLoading && (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={18} />
              <Typography sx={{ color: 'var(--wb-muted-80)' }}>
                Cargando políticas...
              </Typography>
            </Stack>
          )}

          {!policiesLoading && policies.length === 0 && (
            <Alert severity="warning" sx={{ backgroundColor: 'var(--wb-warning-12)', color: 'var(--wb-warning)' }}>
              No hay políticas disponibles todavía. Crea una en la sección "Políticas" antes de componer roles.
            </Alert>
          )}

          {!policiesLoading && policies.length > 0 && (
            <FormGroup>
              {policies.map((policy) => (
                <FormControlLabel
                  key={policy.policy_id}
                  control={
                    <Checkbox
                      checked={formState.policyIds.includes(policy.policy_id)}
                      onChange={() => togglePolicy(policy.policy_id)}
                      sx={{ color: 'var(--wb-muted-60)', '&.Mui-checked': { color: 'var(--wb-accent)' } }}
                    />
                  }
                  label={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <span>{policy.name}</span>
                      {policy.is_system && (
                        <Chip
                          label="Sistema"
                          size="small"
                          sx={{ backgroundColor: 'var(--wb-border-20)', color: 'var(--wb-text-label)' }}
                        />
                      )}
                    </Stack>
                  }
                />
              ))}
            </FormGroup>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={isSubmitting} sx={{ color: 'var(--wb-muted)' }}>
          Cancelar
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={isSubmitting}
          sx={{ bgcolor: 'var(--wb-primary)', '&:hover': { bgcolor: 'var(--wb-primary-hover)' } }}
        >
          {isSubmitting ? 'Guardando...' : mode === 'edit' ? 'Guardar cambios' : 'Crear rol'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const SecurityRoles = () => {
  const { isPlatformRoot, isTenantAdmin, userData, hasPermission } = useContext(DataContext);
  // El acceso a la página completa exige admin de cuenta o de plataforma
  // (administrar el catálogo de roles es una operación de cuenta). Una vez
  // dentro, honramos además el permiso granular "security.roles.manage" del
  // catálogo si el backend lo expone, sin restringir por debajo de lo que
  // ya permite isPlatformRoot/isTenantAdmin.
  const canManageRoleCatalog = Boolean(isPlatformRoot || isTenantAdmin);
  const canEditRoles = canManageRoleCatalog || hasPermission('security.roles.manage');

  // Igual que en UserInvitesPage: userData sólo se setea una vez terminado
  // el fetch inicial de sesión -- se usa como proxy de "ya sabemos el rol
  // real del usuario" para no mostrar "acceso restringido" prematuramente.
  const accessChecked = Boolean(userData);

  const { createRole, updateRole, deleteRole } = useSecurityRolesService();

  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [rolesError, setRolesError] = useState('');

  const [policies, setPolicies] = useState([]);
  const [policiesLoading, setPoliciesLoading] = useState(false);

  const [pendingInvitesCount, setPendingInvitesCount] = useState(null);
  const [pendingInvitesCapped, setPendingInvitesCapped] = useState(false);

  const [feedback, setFeedback] = useState(null);
  const [dialogState, setDialogState] = useState({ open: false, mode: 'create', role: null });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchRoles = useCallback(async () => {
    if (!canManageRoleCatalog) {
      return;
    }
    setRolesLoading(true);
    setRolesError('');
    try {
      const response = await listRoles();
      // El backend devuelve {items, count} -- antes se trataba `response`
      // como si ya fuera el array, lo cual dejaba los roles de sistema
      // (role_admin/role_analyst/role_viewer) invisibles en silencio, igual
      // bug ya corregido para listUsers() en SecurityUsers.jsx.
      setRoles(Array.isArray(response?.items) ? response.items : []);
    } catch (error) {
      setRolesError(error?.message || 'No se pudo obtener el listado de roles.');
    } finally {
      setRolesLoading(false);
    }
  }, [canManageRoleCatalog]);

  const fetchPolicies = useCallback(async () => {
    if (!canManageRoleCatalog) {
      return;
    }
    setPoliciesLoading(true);
    try {
      const response = await listPolicies();
      setPolicies(Array.isArray(response?.items) ? response.items : []);
    } catch (error) {
      console.warn('⚠️ No se pudo cargar el listado de políticas:', error);
      setPolicies([]);
    } finally {
      setPoliciesLoading(false);
    }
  }, [canManageRoleCatalog]);

  const fetchPendingInvites = useCallback(async () => {
    if (!canManageRoleCatalog) {
      return;
    }
    try {
      // Se pide una tanda grande solo para el contador: el backend pagina
      // con cursor y no puede dar un total sin recorrer todo. Si queda
      // cursor, hay más de INVITES_COUNT_CAP y la tarjeta muestra "100+".
      const { items, nextCursor } = await listInvites({
        status: 'PENDING', limit: INVITES_COUNT_CAP
      });
      setPendingInvitesCount(items.length);
      setPendingInvitesCapped(Boolean(nextCursor));
    } catch (error) {
      console.warn('⚠️ No se pudo obtener el conteo de invitaciones pendientes:', error);
      setPendingInvitesCount(null);
    }
  }, [canManageRoleCatalog]);

  useEffect(() => {
    fetchRoles();
    fetchPolicies();
    fetchPendingInvites();
  }, [fetchRoles, fetchPolicies, fetchPendingInvites]);

  const policiesById = useMemo(() => {
    const map = {};
    policies.forEach((policy) => {
      map[policy.policy_id] = policy;
    });
    return map;
  }, [policies]);

  const distinctPoliciesInUseCount = useMemo(() => {
    const set = new Set();
    roles.forEach((role) => {
      (Array.isArray(role.policy_ids) ? role.policy_ids : []).forEach((policyId) => set.add(policyId));
    });
    return set.size;
  }, [roles]);

  const customRolesCount = useMemo(
    () => roles.filter((role) => !role.is_system).length,
    [roles]
  );

  const handleOpenCreate = () => {
    setDialogState({ open: true, mode: 'create', role: null });
  };

  const handleOpenEdit = (role) => {
    setDialogState({ open: true, mode: 'edit', role });
  };

  const handleCloseDialog = () => {
    if (isSubmitting) {
      return;
    }
    setDialogState({ open: false, mode: 'create', role: null });
  };

  const handleSubmitDialog = async (formState) => {
    setIsSubmitting(true);
    setFeedback(null);
    try {
      if (dialogState.mode === 'edit' && dialogState.role) {
        await updateRole(dialogState.role.role_id, formState);
        setFeedback({ severity: 'success', message: `Se actualizó el rol "${formState.name}".` });
      } else {
        await createRole(formState);
        setFeedback({ severity: 'success', message: `Se creó el rol "${formState.name}".` });
      }
      setDialogState({ open: false, mode: 'create', role: null });
      await fetchRoles();
    } catch (error) {
      setFeedback({
        severity: 'error',
        message: error?.message || 'No se pudo guardar el rol.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) {
      return;
    }
    setIsDeleting(true);
    setFeedback(null);
    try {
      await deleteRole(deleteTarget.role_id);
      setFeedback({ severity: 'success', message: `Se eliminó el rol "${deleteTarget.name}".` });
      setDeleteTarget(null);
      await fetchRoles();
    } catch (error) {
      setFeedback({
        severity: 'error',
        message: error?.message || 'No se pudo eliminar el rol.'
      });
    } finally {
      setIsDeleting(false);
    }
  };

  if (!accessChecked) {
    return (
      <PageContainer title="Roles" description="Define roles reutilizables y asigna permisos modulares.">
        <Section title="Cargando acceso">
          <Stack direction="row" spacing={2} alignItems="center">
            <CircularProgress size={24} />
            <Typography sx={{ color: 'var(--wb-text-label)' }}>Verificando permisos...</Typography>
          </Stack>
        </Section>
      </PageContainer>
    );
  }

  if (!canManageRoleCatalog) {
    return (
      <PageContainer
        title="Roles"
        description="Solo administradores de la cuenta pueden gestionar el catálogo de roles."
      >
        <Section title="Acceso restringido">
          <Alert severity="warning" sx={{ backgroundColor: 'var(--wb-warning-12)', color: 'var(--wb-warning)' }}>
            Necesitas privilegios de administrador (de tu cuenta o de la plataforma) para acceder a esta sección.
            Contacta al administrador de la cuenta si crees que se trata de un error.
          </Alert>
        </Section>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      title="Roles"
      description="Define roles reutilizables, asigna permisos modulares y monitorea cambios para auditoría."
      actions={[
        <Button
          key="refresh"
          variant="outlined"
          color="info"
          startIcon={<RefreshIcon />}
          onClick={() => { fetchRoles(); fetchPolicies(); fetchPendingInvites(); }}
          disabled={rolesLoading}
          sx={{ textTransform: 'none', borderColor: 'var(--wb-accent-40)', color: 'var(--wb-accent)' }}
        >
          Actualizar
        </Button>,
        canEditRoles && (
          <Button
            key="role"
            variant="outlined"
            color="secondary"
            onClick={handleOpenCreate}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Nuevo rol
          </Button>
        )
      ].filter(Boolean)}
    >
      {feedback && (
        <Box sx={{ mb: 3 }}>
          <Alert severity={feedback.severity} onClose={() => setFeedback(null)}>
            {feedback.message}
          </Alert>
        </Box>
      )}

      <StatGrid
        items={[
          { label: 'Roles configurados', value: String(roles.length), trend: `${customRolesCount} personalizados` },
          { label: 'Políticas en uso', value: String(distinctPoliciesInUseCount), trend: 'Adjuntas a algún rol' },
          { label: 'Roles personalizados', value: String(customRolesCount), trend: `${roles.length - customRolesCount} de sistema` },
          {
            label: 'Invitaciones pendientes',
            value: pendingInvitesCount === null
              ? '—'
              : `${pendingInvitesCount}${pendingInvitesCapped ? '+' : ''}`,
            trend: pendingInvitesCount === null ? 'No disponible' : 'En espera de aceptación'
          }
        ]}
      />

      {rolesError && (
        <Alert severity="error" sx={{ mb: 2, backgroundColor: 'var(--wb-danger-18)', color: 'var(--wb-danger-soft)' }}>
          {rolesError}
        </Alert>
      )}

      <Section
        title="Roles principales"
        subtitle="Cada rol agrupa políticas y autorizaciones por capas del modelo semántico"
      >
        {rolesLoading && roles.length === 0 && (
          <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 2 }}>
            <CircularProgress size={22} />
            <Typography sx={{ color: 'var(--wb-text-label)' }}>Cargando roles...</Typography>
          </Stack>
        )}

        {!rolesLoading && roles.length === 0 && !rolesError && (
          <Typography sx={{ color: 'var(--wb-muted-70)', py: 2 }}>
            No hay roles configurados todavía.
          </Typography>
        )}

        <Grid container spacing={3}>
          {roles.map((role, index) => {
            const attachedPolicyIds = Array.isArray(role.policy_ids) ? role.policy_ids : [];
            return (
              <Grid item xs={12} md={4} key={role.role_id}>
                <Box
                  sx={{
                    backgroundColor: 'var(--wb-bg-surface)',
                    borderRadius: 2,
                    border: '1px solid var(--wb-border-10)',
                    p: 3,
                    height: '100%'
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      {ROLE_ICONS[index % ROLE_ICONS.length]}
                      <Box>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography sx={{ color: 'var(--wb-text-title)', fontWeight: 600 }}>{role.name}</Typography>
                          {role.is_system && (
                            <Chip
                              label="Rol de sistema"
                              size="small"
                              sx={{ backgroundColor: 'var(--wb-border-20)', color: 'var(--wb-text-label)' }}
                            />
                          )}
                        </Stack>
                        <Typography sx={{ color: 'var(--wb-muted-76)', fontSize: '0.85rem' }}>
                          {role.description}
                        </Typography>
                      </Box>
                    </Stack>
                    {!role.is_system && canEditRoles && (
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Editar rol">
                          <IconButton size="small" onClick={() => handleOpenEdit(role)} sx={{ color: 'var(--wb-accent)' }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar rol">
                          <IconButton size="small" onClick={() => setDeleteTarget(role)} sx={{ color: 'var(--wb-danger)' }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    )}
                  </Stack>
                  <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
                    {attachedPolicyIds.length === 0 && (
                      <Typography sx={{ color: 'var(--wb-muted-60)', fontSize: '0.8rem' }}>
                        Sin políticas asignadas
                      </Typography>
                    )}
                    {attachedPolicyIds.map((policyId) => (
                      <Chip
                        key={policyId}
                        label={policiesById[policyId]?.name || policyId}
                        size="small"
                        sx={{
                          backgroundColor: 'var(--wb-border-16)',
                          color: 'var(--wb-text-label)',
                          width: 'fit-content'
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Section>

      <Section
        title="Workflows de aprobación"
        subtitle="Automatiza revisiones de acceso y documenta cada modificación"
      >
        <List>
          {[
            {
              title: 'Solicitudes de acceso temporal',
              detail: 'Workflow de 2 pasos con aprobación del owner del dominio'
            },
            {
              title: 'Cambios en políticas críticas',
              detail: 'Revisión cruzada con seguridad + notificación automática a auditoría'
            }
          ].map((workflow) => (
            <ListItem key={workflow.title} sx={{ px: 0 }}>
              <Stack spacing={0.8}>
                <Typography sx={{ color: 'var(--wb-text-title)', fontWeight: 600 }}>{workflow.title}</Typography>
                <Typography sx={{ color: 'var(--wb-muted-76)' }}>{workflow.detail}</Typography>
              </Stack>
            </ListItem>
          ))}
        </List>
      </Section>

      <RoleFormDialog
        open={dialogState.open}
        mode={dialogState.mode}
        initialRole={dialogState.role}
        policies={policies}
        policiesLoading={policiesLoading}
        onClose={handleCloseDialog}
        onSubmit={handleSubmitDialog}
        isSubmitting={isSubmitting}
      />

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => (isDeleting ? undefined : setDeleteTarget(null))}
        PaperProps={{ sx: { backgroundColor: 'var(--wb-bg-surface)', border: '1px solid var(--wb-border-16)' } }}
      >
        <DialogTitle sx={{ color: 'var(--wb-text-title)' }}>Eliminar rol</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'var(--wb-text-label)' }}>
            ¿Seguro que deseas eliminar el rol "{deleteTarget?.name}"? Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={isDeleting} sx={{ color: 'var(--wb-muted)' }}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            disabled={isDeleting}
          >
            {isDeleting ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default SecurityRoles;
